// Central pre-upload compression for skip attachments.
//
// Called by the attachment upload hook before building the multipart
// body. The browser does the heavy lifting via Canvas — no extra deps
// needed. Behaviour:
//
//   - JPEG / PNG / WebP: re-encode through a canvas at most
//     MAX_DIMENSION on the longest edge and quality 0.85. JPEG +
//     WebP outputs use their native encoders; PNG inputs become
//     JPEG (transparent → black) since photos compress dramatically
//     better and proof-of-delivery photos rarely need transparency.
//   - PDF + anything else: pass-through (we don't compress documents
//     client-side — that's the user's responsibility).
//
// The util always returns a `File`. If compression fails for any
// reason (canvas tainted, out-of-memory, etc.) we fall back to the
// original file rather than blocking the upload.
//
// Tunables live at the top so they're easy to tweak without
// reading the implementation.

const MAX_DIMENSION = 2000;
const JPEG_QUALITY = 0.85;
const COMPRESSIBLE_MIMES = ['image/jpeg', 'image/png', 'image/webp'] as const;

type CompressibleMime = (typeof COMPRESSIBLE_MIMES)[number];

function isCompressible(mime: string): mime is CompressibleMime {
  return (COMPRESSIBLE_MIMES as readonly string[]).includes(mime);
}

/**
 * Compress a single file if it's a supported image type. Pass-through
 * for everything else. The output filename mirrors the input but
 * with the extension swapped to match the encoder's chosen mime.
 */
export async function compressFileForUpload(file: File): Promise<File> {
  if (!isCompressible(file.type)) return file;

  try {
    const bitmap = await loadImage(file);
    const { width, height } = scaleDownTo(MAX_DIMENSION, bitmap.width, bitmap.height);

    // Skip the round-trip when the image is already small AND the
    // input is already JPEG — re-encoding gives marginal gains and
    // we keep the original orientation metadata intact.
    if (
      width === bitmap.width &&
      height === bitmap.height &&
      file.type === 'image/jpeg'
    ) {
      return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (ctx === null) return file;
    ctx.drawImage(bitmap, 0, 0, width, height);

    // Choose an output mime: keep WebP as WebP, otherwise JPEG.
    // PNGs become JPEGs (see top-of-file note) — user keeps a
    // significantly smaller upload for photographic content.
    const outMime: CompressibleMime =
      file.type === 'image/webp' ? 'image/webp' : 'image/jpeg';
    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), outMime, JPEG_QUALITY),
    );
    if (blob === null) return file;

    // If the round-trip somehow grew the file, prefer the original.
    if (blob.size >= file.size) return file;

    const newName = swapExtension(file.name, outMime === 'image/webp' ? 'webp' : 'jpg');
    return new File([blob], newName, { type: outMime, lastModified: Date.now() });
  } catch {
    // Canvas can fail silently in older browsers / on huge images.
    // Falling back to the original means the upload still succeeds
    // (the server enforces its own size cap separately).
    return file;
  }
}

/** Compress a list of files in parallel. */
export async function compressFilesForUpload(
  files: ReadonlyArray<File>,
): Promise<File[]> {
  return Promise.all(files.map((f) => compressFileForUpload(f)));
}

// -- helpers ---------------------------------------------------------------

async function loadImage(file: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('image load failed'));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function scaleDownTo(
  maxDim: number,
  width: number,
  height: number,
): { width: number; height: number } {
  if (width <= maxDim && height <= maxDim) return { width, height };
  const ratio = width >= height ? maxDim / width : maxDim / height;
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

function swapExtension(filename: string, newExt: string): string {
  const dot = filename.lastIndexOf('.');
  if (dot <= 0) return `${filename}.${newExt}`;
  return `${filename.slice(0, dot)}.${newExt}`;
}
